import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  startTransition,
} from 'react';
import PropTypes from 'prop-types';

const ModalContext = createContext(null);

// Action types
const MODAL_ACTIONS = {
  OPEN: 'OPEN_MODAL',
  CLOSE: 'CLOSE_MODAL',
  SET_LOADING: 'SET_MODAL_LOADING',
  UPDATE_DATA: 'UPDATE_MODAL_DATA',
  CLOSE_ALL: 'CLOSE_ALL_MODALS',
};

// Reducer function
function modalReducer(state, action) {
  switch (action.type) {
    case MODAL_ACTIONS.OPEN:
      return {
        ...state,
        [action.payload.modalId]: {
          isOpen: true,
          data: action.payload.data,
          isLoading: false,
        },
      };

    case MODAL_ACTIONS.CLOSE:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          isOpen: false,
        },
      };

    case MODAL_ACTIONS.SET_LOADING:
      return {
        ...state,
        [action.payload.modalId]: {
          ...state[action.payload.modalId],
          isLoading: action.payload.isLoading,
        },
      };

    case MODAL_ACTIONS.UPDATE_DATA:
      return {
        ...state,
        [action.payload.modalId]: {
          ...state[action.payload.modalId],
          data: {
            ...state[action.payload.modalId]?.data,
            ...action.payload.data,
          },
        },
      };

    case MODAL_ACTIONS.CLOSE_ALL:
      return Object.keys(state).reduce((acc, key) => {
        acc[key] = {
          ...state[key],
          isOpen: false,
        };
        return acc;
      }, {});

    default:
      return state;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const [modals, dispatch] = useReducer(modalReducer, {});

  const openModal = useCallback((modalId, modalData = {}) => {
    startTransition(() => {
      dispatch({
        type: MODAL_ACTIONS.OPEN,
        payload: { modalId, data: modalData },
      });
    });
  }, []);

  const closeModal = useCallback((modalId) => {
    startTransition(() => {
      dispatch({
        type: MODAL_ACTIONS.CLOSE,
        payload: modalId,
      });
    });
  }, []);

  const isModalOpen = useCallback(
    (modalId) => {
      return modals[modalId]?.isOpen || false;
    },
    [modals],
  );

  const getModalData = useCallback(
    (modalId) => {
      return modals[modalId]?.data || {};
    },
    [modals],
  );

  const setModalLoading = useCallback((modalId, isLoading) => {
    startTransition(() => {
      dispatch({
        type: MODAL_ACTIONS.SET_LOADING,
        payload: { modalId, isLoading },
      });
    });
  }, []);

  const isModalLoading = useCallback(
    (modalId) => {
      return modals[modalId]?.isLoading || false;
    },
    [modals],
  );

  const updateModalData = useCallback((modalId, data) => {
    startTransition(() => {
      dispatch({
        type: MODAL_ACTIONS.UPDATE_DATA,
        payload: { modalId, data },
      });
    });
  }, []);

  const closeAllModals = useCallback(() => {
    startTransition(() => {
      dispatch({ type: MODAL_ACTIONS.CLOSE_ALL });
    });
  }, []);

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
      isModalOpen,
      getModalData,
      setModalLoading,
      isModalLoading,
      updateModalData,
      closeAllModals,
    }),
    [
      openModal,
      closeModal,
      isModalOpen,
      getModalData,
      setModalLoading,
      isModalLoading,
      updateModalData,
      closeAllModals,
    ],
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ModalContext;
